import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Star, X } from 'lucide-react';
import Combobox from '../../components/ui/Combobox';
import KitTooltip from '../../components/ui/Tooltip';
import { controlResetClassName, cx } from '../../components/ui/classNames';
import { MAX_MARKETS, MAX_PORTFOLIOS } from './exploreColumns';

const CHIP_CLASS_NAME = 'inline-flex h-7 max-w-[220px] items-center gap-1 rounded-full border border-solid border-ui-line '
    + 'bg-ui-surface py-0 pl-1.5 pr-1 text-[12px] font-medium text-ui-ink';
const CHIP_BUTTON_CLASS_NAME = cx(
    controlResetClassName,
    'inline-flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent p-0',
    'text-ui-muted hover:bg-ui-hover hover:text-ui-ink focus:outline-none focus-visible:outline focus-visible:outline-2',
    'focus-visible:outline-ui-focus disabled:cursor-default',
);

// The markets the field can add (those with history), at most MAX_MARKETS beside any number of portfolios.
function MarketChips({ options, value, onChange, disabled }) {
    const [query, setQuery] = useState('');
    const labelFor = (key) => options.find((option) => option.key === key)?.label ?? key;
    const remaining = options.filter((option) => option.available !== false && !value.includes(option.key));
    const add = (text) => {
        const option = remaining.find((item) => item.label === text);
        if (!option) return;
        setQuery('');
        onChange([...value, option.key]);
    };
    return (
        <div role="group" aria-label="Markets beside the portfolios" className="flex min-w-0 flex-wrap items-center gap-1.5">
            <span className="text-[11px] font-semibold text-ui-muted">Markets</span>
            {value.map((key) => {
                const label = labelFor(key);
                return (
                    <span key={key} className={cx(CHIP_CLASS_NAME, '!border-dashed pl-2.5')}>
                        <KitTooltip content={`${label}: the ETF's actual return, shown beside the replays. Never the baseline.`}>
                            <span tabIndex={0} className="min-w-0 truncate" title={label}>{label}</span>
                        </KitTooltip>
                        <button type="button" className={CHIP_BUTTON_CLASS_NAME} aria-label={`Remove ${label}`} disabled={disabled}
                            onClick={() => onChange(value.filter((item) => item !== key))}>
                            <X aria-hidden="true" size={13} strokeWidth={2.25} />
                        </button>
                    </span>
                );
            })}
            {value.length < MAX_MARKETS && remaining.length > 0 && (
                <Combobox
                    aria-label="Add a market"
                    placeholder="+ Add market"
                    inputValue={query}
                    onInputChange={setQuery}
                    options={remaining.map((option) => option.label)}
                    filterOnOpen
                    onCommit={add}
                    disabled={disabled}
                    className="!h-7 !w-40 !rounded-full !text-[12px]"
                />
            )}
            {value.length >= MAX_MARKETS && <span className="text-[11px] text-ui-muted">Up to {MAX_MARKETS} markets</span>}
        </div>
    );
}

const MARKET_OPTION = PropTypes.shape({ key: PropTypes.string.isRequired, label: PropTypes.string.isRequired, available: PropTypes.bool });

MarketChips.propTypes = {
    options: PropTypes.arrayOf(MARKET_OPTION).isRequired,
    value: PropTypes.arrayOf(PropTypes.string).isRequired,
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool.isRequired,
};

/**
 * The portfolios in a comparison, as chips: the first is the baseline (a
 * filled star); another chip's star makes it the baseline, × removes it, and
 * the field after the chips adds one of the remaining `options` (at most
 * `max`). `value` is the chosen option keys, baseline first; `onChange`
 * reports the next keys. The last chip cannot be removed.
 *
 * Beside them, a Markets group holds `markets` (keys of `marketOptions`,
 * ETFs whose actual returns are shown, at most three), added and
 * removed the same way through `onMarketsChange`; only options not marked
 * `available: false` can be added. A market is never the baseline and does
 * not count toward `max`.
 */
export default function PortfolioChipPicker({
    options, value, onChange, max = MAX_PORTFOLIOS, disabled = false,
    marketOptions = [], markets = [], onMarketsChange = null,
}) {
    const [query, setQuery] = useState('');
    const labelFor = (key) => options.find((option) => option.key === key)?.label ?? 'Unavailable portfolio';
    const remaining = options.filter((option) => !value.includes(option.key));
    const add = (text) => {
        const option = remaining.find((item) => item.label === text);
        if (!option) return;
        setQuery('');
        onChange([...value, option.key]);
    };
    return (
        <>
            <div role="group" aria-label="Portfolios in this comparison" className="flex min-w-0 flex-wrap items-center gap-1.5">
                {value.map((key, index) => {
                    const label = labelFor(key);
                    const baseline = index === 0;
                    return (
                        <span key={key} className={CHIP_CLASS_NAME} data-baseline={baseline || undefined}>
                            <KitTooltip content={baseline ? 'Baseline: the vs-baseline view and the worst and best periods use this portfolio' : `Make ${label} the baseline`}>
                                <button
                                    type="button"
                                    className={cx(CHIP_BUTTON_CLASS_NAME, baseline && '!text-ui-warning')}
                                    aria-label={baseline ? `${label} is the baseline` : `Make ${label} the baseline`}
                                    aria-pressed={baseline}
                                    disabled={disabled}
                                    onClick={() => { if (!baseline) onChange([key, ...value.filter((item) => item !== key)]); }}
                                >
                                    <Star aria-hidden="true" size={13} strokeWidth={2.25} fill={baseline ? 'currentColor' : 'none'} />
                                </button>
                            </KitTooltip>
                            <span className="min-w-0 truncate" title={label}>{label}</span>
                            {value.length > 1 && (
                                <button
                                    type="button"
                                    className={CHIP_BUTTON_CLASS_NAME}
                                    aria-label={`Remove ${label}`}
                                    disabled={disabled}
                                    onClick={() => onChange(value.filter((item) => item !== key))}
                                >
                                    <X aria-hidden="true" size={13} strokeWidth={2.25} />
                                </button>
                            )}
                        </span>
                    );
                })}
                {value.length < max && remaining.length > 0 && (
                    <Combobox
                        aria-label="Add a portfolio"
                        placeholder="+ Add portfolio"
                        inputValue={query}
                        onInputChange={setQuery}
                        options={remaining.map((option) => option.label)}
                        filterOnOpen
                        onCommit={add}
                        disabled={disabled}
                        className="!h-7 !w-44 !rounded-full !text-[12px]"
                    />
                )}
                {value.length >= max && <span className="text-[11px] text-ui-muted">Up to {max} portfolios</span>}
            </div>
            {onMarketsChange && (markets.length > 0 || marketOptions.some((option) => option.available !== false)) && (
                <MarketChips options={marketOptions} value={markets} onChange={onMarketsChange} disabled={disabled} />
            )}
        </>
    );
}

PortfolioChipPicker.propTypes = {
    options: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string.isRequired, label: PropTypes.string.isRequired })).isRequired,
    value: PropTypes.arrayOf(PropTypes.string).isRequired,
    onChange: PropTypes.func.isRequired,
    max: PropTypes.number,
    disabled: PropTypes.bool,
    marketOptions: PropTypes.arrayOf(MARKET_OPTION),
    markets: PropTypes.arrayOf(PropTypes.string),
    onMarketsChange: PropTypes.func,
};
